import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material';
import * as moment from 'moment-timezone';
import { reject } from 'lodash';
import { Subscription, combineLatest as observableCombineLatest } from 'rxjs';

import { parseErrors } from '../shared/api.service';
import { PunchCardService } from './punch-card.service';
import { JobService } from '../jobs/job.service';
import { JobEventService } from '../job-events/job-event.service';
import { DriverService } from '../drivers/driver.service';
import { TruckService } from '../trucks/truck.service';
import { OrganizationService } from '../organizations/organization.service';
import { Job } from '../jobs/job';
import { Carrier } from '../carriers/carrier';
import { Truck } from '../trucks/truck';
import { Driver } from '../drivers/driver';
import { JobEvent } from '../job-events/job-event';
import { PunchCard } from './punch-card';
import { JobSerializer } from '../jobs/job.serializer';
import { ProjectSerializer } from '../projects/project.serializer';
import { TranslateService } from '@ngx-translate/core';
import { RuckitDropdownComponent } from '../shared/ruckit-dropdown/ruckit-dropdown.component';

@Component({
  selector: 'new-punch-card',
  templateUrl: './new-punch-card.component.html',
  styleUrls: ['./new-punch-card.component.scss']
})
export class NewPunchCardComponent implements OnInit, OnDestroy {
  errors = [];
  jobEventError: string = null;
  ticketImageFile = {
    dataUri: null,
    file: null
  };

  job: Job;
  jobId: string;
  carrier: Carrier;
  truck: Truck;
  driver: Driver;
  jobEvent: JobEvent;
  jobEventId: string;
  punchCard: PunchCard = new PunchCard({});
  jobDates: Date[] = [];
  callback;

  jobReq: Subscription;
  punchCardReq: Subscription;
  daysReq: Subscription;
  jobsReq: Subscription;
  jobEventReq: Subscription;
  jobEventsReq: Subscription;
  trucksReq: Subscription;
  driversReq: Subscription;

  returnTo: string;
  loading = false;
  daysLoading = false;
  jobEventsLoading = false;

  @ViewChild('jobDropdown', { static: false }) jobDropdown;
  @ViewChild('carriersDropdown', { static: false })
  carriersDropdown: RuckitDropdownComponent;
  @ViewChild('driverDropdown', { static: false }) driverDropdown;
  @ViewChild('truckDropdown', { static: false }) truckDropdown;
  @ViewChild('jobEventsDropdown', { static: false }) jobEventsDropdown;

  jobDropdownOptions = [];
  jobDropdownConfig = {
    nameProperty: 'nameWithDate',
    searchable: true,
    group: true,
    groupBy: job => job.group,
    loadingOptions: false
  };
  driverDropdownOptions = [];
  driverDropdownConfig = {
    nameProperty: 'name',
    searchable: true,
    loadingOptions: false
  };
  truckDropdownOptions = [];
  truckDropdownConfig = {
    nameProperty: 'displayName',
    searchable: true,
    loadingOptions: false
  };

  carrierDropdownConfig = {
    nameProperty: 'name',
    idProperty: 'carrier.id',
    selectText: this.translateService.instant('Select Carrier'),
    loadingText: this.translateService.instant('Loading Carriers...'),
    noResultsText: this.translateService.instant('No Carriers'),
    service: OrganizationService,
    serviceFunction: 'get',
    query: {
      exclude: null,
    },
    includeFullObject: true,
    prefilledOptions: [],
  };
  jobEventsDropdownOptions = [];
  jobEventsDropdownConfig = {
    nameProperty: 'name',
    searchable: true,
    loadingOptions: false
  };
  allSubscriptionsToUnsubscribe: Subscription[] = [];

  constructor (
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private punchCardService: PunchCardService,
    private jobService: JobService,
    private jobEventService: JobEventService,
    private driverService: DriverService,
    private truckService: TruckService,
    private organizationService: OrganizationService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.loading = true;

    let combinedParams = observableCombineLatest(
      this.route.params, this.route.queryParams,
      (params, qparams) => ({ params, qparams })
    );

    this.getJobs();

    this.allSubscriptionsToUnsubscribe.push(
      combinedParams.subscribe(result => {
        this.returnTo = result && result.qparams['returnTo'];
        if (result && result.params['id']) {
          this.jobId = result.params['jobId'];
          this.jobEventId = result.params['jobEventId'];
          this.prefillPunchCard(this.jobId, this.jobEventId);
        }
      })
    );
  }

  ngOnDestroy() {
    if (this.jobReq && typeof this.jobReq.unsubscribe === 'function') {
      this.jobReq.unsubscribe();
    }
    if (this.jobsReq && typeof this.jobsReq.unsubscribe === 'function') {
      this.jobsReq.unsubscribe();
    }
    if (this.jobEventReq && typeof this.jobEventReq.unsubscribe === 'function') {
      this.jobEventReq.unsubscribe();
    }
    if (this.jobEventsReq && typeof this.jobEventsReq.unsubscribe === 'function') {
      this.jobEventsReq.unsubscribe();
    }
    if (this.punchCardReq && typeof this.punchCardReq.unsubscribe === 'function') {
      this.punchCardReq.unsubscribe();
    }
    if (this.daysReq && typeof this.daysReq.unsubscribe === 'function') {
      this.daysReq.unsubscribe();
    }
    if (this.driversReq && typeof this.driversReq.unsubscribe === 'function') {
      this.driversReq.unsubscribe();
    }
    if (this.trucksReq && typeof this.trucksReq.unsubscribe === 'function') {
      this.trucksReq.unsubscribe();
    }
    this.allSubscriptionsToUnsubscribe.forEach(sub => {
      sub.unsubscribe();
    });
  }

  submitPunchCard() {
    this.loading = true;
    this.errors = [];
    this.punchCardService.saveWithFile(
      this.punchCard, this.ticketImageFile.file
    ).subscribe(() => {
      this.loading = false;
      if (this.returnTo) {
        this.router.navigate([this.returnTo]);
      } else {
        this.router.navigate(['/punchcards']);
      }
    }, (err) => {
      this.errors = parseErrors(err);
      this.loading = false;
    });
  }

  prefillPunchCard(jobId, jobEventId) {
    this.getJob(jobId);
  }

  onStartDateChanged(dates: Date[]) {
    this.punchCard.startDate = dates[0];
  }

  onEndDateChanged(dates: Date[]) {
    this.punchCard.endDate = dates[0];
  }

  onStartTimeChange(value: any) {
    const time = value.target.value;
    this.punchCard.startTime = time;
  }

  onEndTimeChange(value: any) {
    const time = value.target.value;
    this.punchCard.endTime = time;
  }

  onSelect(filterType: string, e): void {
    switch (filterType) {
      case 'job':
        this.job = e;
        this.getJobDays(this.job && this.job.id);
        this.getJobEvents({});
        break;
      case 'jobevent':
        this.jobEvent = e;
        const query = {
          ...this.carrierDropdownConfig.query,
          carriers_for_jobevent: e.id,
        };

        this.carriersDropdown.config.query = query;
        this.carriersDropdown.getRecords(query);
        break;
      case 'driver':
        this.driver = e;
        break;
      case 'truck':
        this.truck = e;
        break;
      case 'carrier':
        this.carrier = e;
        this.getDrivers();
        this.getTrucks();
        break;
    }
    this.punchCard[filterType] = e;
  }

  getJob(id: string): void {
    this.jobDropdownOptions = [];

    if (this.jobReq && typeof this.jobReq.unsubscribe === 'function') {
      this.jobReq.unsubscribe();
    }

    if (id) {
      this.loading = true;
      this.jobReq = this.jobService.get(id).subscribe(job => {
        this.job = job;
        if (this.job) {
          this.jobDropdownOptions = reject(this.jobDropdownOptions, this.job);
          this.jobDropdownOptions.unshift(this.job);
          this.getJobDays(this.job && this.job.id);
          this.jobDropdown.selectedOption = this.job;
        } else {
          let project = new ProjectSerializer().fromJson({ id: this.job.project.id, name: this.job.project.name });
          this.job = new JobSerializer().fromJson({ id: this.job.id, name: this.job.name, project: project });
          this.jobDropdownOptions.unshift(this.job);
          this.getJobDays(this.job && this.job.id);
          this.jobDropdown.selectedOption = this.job;
        }
        this.loading = false;
      }, err => {
        this.errors = err;
        this.loading = false;
      });
    }
  }

  getJobDays(jobId: string): void {
    if (this.daysReq && typeof this.daysReq.unsubscribe === 'function') {
      this.daysReq.unsubscribe();
    }

    this.daysLoading = true;

    this.daysReq = this.jobService.getDays(jobId).subscribe(days => {
      this.jobDates = days.map(day => moment(day).toDate());
      this.daysLoading = false;
    }, err => {
      this.errors = err;
      this.daysLoading = false;
    });
  }

  getJobs(query = {}): void {
    this.jobDropdownOptions = [];
    if (this.jobsReq && typeof this.jobsReq.unsubscribe === 'function') {
      this.jobsReq.unsubscribe();
    }

    let today = new Date();
    today.setHours(0, 0, 0, 0);
    this.jobsReq = this.jobService.list({
      ordering: 'project__name,name,start_date',
      serializer: 'JobGroupedDropdown',
      ...query
    }).subscribe(jobs => {
      this.jobDropdownOptions = this.jobDropdownOptions.concat(jobs);
    }, err => {
      this.errors = err;
    }, () => {
      this.loading = false;
    });
  }

  getJobEvents(query = {}): void {
    this.loading = true;
    this.jobEventsLoading = true;
    this.jobEventError = null;
    this.jobEventsDropdownOptions = [];

    if (this.jobEventsReq && typeof this.jobEventsReq.unsubscribe === 'function') {
      this.jobEventsReq.unsubscribe();
    }

    this.jobEventsReq = this.jobEventService.getJobEvents({
      job: this.job && this.job.id || this.jobId,
      ordering: '-shift1_start',
      page_size: 25,
      ...query
    }).subscribe(jobEvents => {
      if (jobEvents && jobEvents.length) {
        this.jobEventsDropdownOptions = jobEvents;
      } else {
        this.jobEventError = 'No Job Event found for that date!';
      }
      this.jobEventsLoading = false;
    }, err => {
      this.errors = err;
      this.jobEventsLoading = false;
    }, () => {
      this.loading = false;
    });
  }

  getDrivers(query = {}): void {
    this.driverDropdownOptions = [];
    if (this.driversReq && typeof this.driversReq.unsubscribe === 'function') {
      this.driversReq.unsubscribe();
    }

    this.driversReq = this.driverService.list({
      ordering: 'name',
      carrier: this.carrier && this.carrier.id,
      ...query
    }).subscribe(drivers => {
      this.driverDropdownOptions = drivers;
    }, err => this.errors = err,
      () => {
        this.loading = false;
      });
  }

  getTrucks(query = {}): void {
    this.truckDropdownOptions = [];
    if (this.trucksReq && typeof this.trucksReq.unsubscribe === 'function') {
      this.trucksReq.unsubscribe();
    }

    this.trucksReq = this.truckService.list({
      ordering: 'name',
      carrier: this.carrier && this.carrier.id,
      shared_jobevent: this.jobEvent && this.jobEvent.id,
      ...query
    }).subscribe(trucks => {
      this.truckDropdownOptions = trucks;
    }, err => this.errors = err,
      () => {
        this.loading = false;
      });
  }

  dropdownSearch(term: string, type: string): void {
    switch (type) {
      case 'job':
        this.getJobs({ search: term });
        break;
      case 'jobEvents':
        this.getJobEvents({ search: term });
        break;
      case 'truck':
        this.getTrucks({ search: term });
        break;
      case 'driver':
        this.getDrivers({ search: term });
        break;
      case 'carrier':

        break;
    }
  }

  dropdownNextPage(e, type: string): void {
    let config, service, options, o;

    switch (type) {
      case 'job':
        config = this.jobDropdownConfig;
        service = this.jobService;
        options = this.jobDropdownOptions;
        o = service.listNext();
        break;
      case 'jobEvents':
        config = this.jobEventsDropdownConfig;
        service = this.jobEventService;
        options = this.jobEventsDropdownOptions;
        o = service.listNext();
        break;
      case 'carrier':
        break;
      case 'driver':
        config = this.driverDropdownConfig;
        service = this.driverService;
        options = this.driverDropdownOptions;
        o = service.listNext();
        break;
      case 'truck':
        config = this.truckDropdownConfig;
        service = this.truckService;
        options = this.truckDropdownOptions;
        o = service.listNext();
        break;
    }

    if (!config.loadingOptions) {
      if (o) {
        config.loadingOptions = true;
        o.subscribe(results => {
          switch (type) {
            case 'job':
              this.jobDropdownOptions = this.jobDropdownOptions.concat(results);
              break;
            case 'jobEvents':
              this.jobEventsDropdownOptions = this.jobEventsDropdownOptions.concat(results);
              break;
            case 'carrier':
              break;
            case 'driver':
              this.driverDropdownOptions = this.driverDropdownOptions.concat(results);
              break;
            case 'truck':
              this.truckDropdownOptions = this.truckDropdownOptions.concat(results);
              break;
          }
          config.loadingOptions = false;
        }, (err) => {
          this.errors = parseErrors(err);
          config.loadingOptions = false;
        });
      }
    }
  }

  fileChange(e) {
    let checkin;

    checkin = this.ticketImageFile;

    let reader = new FileReader();
    reader.onload = function (loadEvent: any) {
      checkin.dataUri = loadEvent.target.result;
    };

    let file = e.srcElement.files[0];
    checkin.file = file;
    reader.readAsDataURL(file);
  }
}
